import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'
import type {
  CustomListProps,
  OptionItem as CustomSelectOptionItem,
} from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import type { CustomSelectProps } from '@wartek-id/select'

import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'

type IndikatorPerencanaanSelectProps = {
  contents: {
    label?: string
    orderNumber?: number
    title: string
    upper?: React.ReactNode
  }
  allowReset?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  options: Array<OptionItem>
  selected?: OptionItem
  customDisplayValue?: CustomSelectProps['customDisplayValue']
  customListItem?: CustomSelectProps['customList']
  onChange: (selected: OptionItem | undefined) => void
}

// New option type to have a better ergonomics
export type OptionItem = {
  label: string
  value: string | number
}

export function IndikatorPerencanaanSelect({
  contents,
  allowReset = true,
  isLoading,
  isDisabled,
  options,
  selected,
  customDisplayValue = DisplayValue,
  customListItem = ListItem,
  onChange,
}: IndikatorPerencanaanSelectProps) {
  const handleReset = () => {
    onChange(undefined)
  }

  const handleChange = (selected: CustomSelectOptionItem) => {
    onChange(toOptionItem(selected))
  }

  // Cast the options to the CustomSelectOptionItem type
  const castedOptions: Array<CustomSelectOptionItem> = useMemo(
    () => options.map(toCustomSelectOptionItem),
    [options]
  )

  return (
    <IndikatorPerencanaanPanel
      variant="active"
      label={contents.label}
      orderNumber={contents.orderNumber}
      title={contents.title}
      upperContent={contents.upper}
    >
      <>
        <div className="flex gap-4 w-full">
          <div className="w-full">
            <CustomSelect
              className="w-full"
              isDisabled={isLoading || isDisabled}
              options={castedOptions}
              placeholder="Pilih di sini"
              value={selected ? toCustomSelectOptionItem(selected) : undefined}
              customDisplayValue={customDisplayValue}
              customList={customListItem}
              onChange={handleChange}
            />
          </div>
          {allowReset && (
            <Button
              color="white"
              disabled={isLoading || isDisabled}
              onClick={handleReset}
            >
              Hapus
            </Button>
          )}
        </div>
      </>
    </IndikatorPerencanaanPanel>
  )
}

function DisplayValue({ item }: { item: CustomSelectOptionItem }) {
  return (
    <Text variant="body" className="text-justify" as="span">
      {item.label}
    </Text>
  )
}

function ListItem({ item, selected, active }: CustomListProps) {
  return (
    <div className="flex items-center gap-2 pr-4">
      <Text variant="body" color={active ? 'inverse' : 'default'} as="span">
        {(item as CustomSelectOptionItem).label}
      </Text>
      {selected && (
        <Icon
          fontSize="sm"
          color={active ? 'inverse' : 'informational'}
          className="absolute right-1.5"
        >
          check
        </Icon>
      )}
    </div>
  )
}

function toCustomSelectOptionItem(option: OptionItem): CustomSelectOptionItem {
  return {
    name: String(option.value),
    label: option.label,
  }
}

function toOptionItem(option: CustomSelectOptionItem): OptionItem {
  return {
    label: option.label,
    value: option.name,
  }
}
